import { EN_TEMPLATES, formatMessage, type Message, type MessageCode } from '@domain';
import type { Lang } from './index.js';

/**
 * Arabic templates for messages that carry a value.
 *
 * Each language holds the whole sentence with a gap in it, so Arabic puts the
 * number or the reference where Arabic puts them. The alternative — translating
 * "Reference", "already exists in Xero" and joining them around the value —
 * produces English word order wearing Arabic words, which reads wrong to a
 * native speaker even though every term is correct.
 */
const AR_TEMPLATES: Record<MessageCode, string> = {
  checks_passed: 'كل الفحوصات ناجحة',
  checks_failed: 'لم تنجح فحوصات ما قبل الإرسال',
  duplicate_reference: 'المرجع {reference} موجود مسبقاً في Xero',
  line_missing_account: 'السطر {line} بدون رمز حساب',
  lines_missing_description: '{count} سطر بدون وصف',
  lines_missing_account: '{count} سطر بدون رمز حساب',
  lines_invalid_tax: '{count} سطر بنسبة ضريبة غير متاحة لهذه المؤسسة',
  line_count: '{count} سطر',
  tax_rates_cached: '{count} نسب ضريبية من القائمة المحفوظة',
  no_reference_entered: 'لم يُدخل مرجع — تم التخطي',
  no_match_for_reference: 'لا يوجد تطابق للمرجع {reference}',
  arabic_digits_folded: 'عُثر على أرقام عربية وحُوّلت قبل الربط',
  digits_normalised: 'يُحوَّل ما يُكتَب من أرقام؛ والواجهة تعرض الأرقام الإنجليزية في كل الشاشات',
  contact_resolved: '{name}',
  no_contact_selected: 'لم تُحدد جهة اتصال',
  from_chart_of_accounts: 'من دليل الحسابات',
  document_created: 'أُنشئت مسودة في Xero',
  rate_limited: 'Xero يحد من المعدل؛ إعادة المحاولة خلال {seconds} ثانية',
};

/** Render a message in the chosen language, with its values slotted in. */
export function renderMessageIn(lang: Lang, msg: Message): string {
  const templates = lang === 'ar' ? AR_TEMPLATES : EN_TEMPLATES;
  return formatMessage(templates[msg.code] ?? EN_TEMPLATES[msg.code], msg.values);
}

export type RenderMessage = (msg: Message) => string;

export function createMessageRenderer(lang: Lang): RenderMessage {
  return (msg) => renderMessageIn(lang, msg);
}
